<script lang="ts">
import { capture } from '$lib/analytics';
import Displacement from '$lib/components/canvasui/Displacement.svelte';
import { Button } from '$lib/components/ui/button/index.js';
import {
  DialogContent,
  DialogHeader,
  Dialog as DialogRoot,
  DialogTitle,
} from '$lib/components/ui/dialog/index.js';
import { auth } from '../../stores/auth.svelte';
import { showToast } from '../../stores/toast.svelte';
import { ui } from '../../stores/ui.svelte';

// Displacement reacts to fast cursor movements over the grid.
// On devices without fine pointers or when prefers-reduced-motion is set,
// we fall back to the compact modal.
const media = (q: string) =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(q).matches;
const displacementEnabled = media('(pointer: fine)') && !media('(prefers-reduced-motion: reduce)');

// `forced` = the app-wide sign-in gate (App.svelte renders this instead of
// the shell when there is no session). In that mode the dialog cannot be
// dismissed: no close button, Escape and outside-clicks ignored.
let { forced = false }: { forced?: boolean } = $props();

let email = $state('');
let sending = $state(false);
let sent = $state(false);
let error = $state<string | null>(null);

function close() {
  if (forced) return;
  ui.authOpen = false;
  error = null;
  sent = false;
}

async function google() {
  error = null;
  capture('login_attempted', { method: 'google' });
  const { error: e } = await auth.signInWithGoogle();
  if (e) {
    capture('login_failed', { method: 'google', error: e });
    error = e;
  }
}

async function magicLink(ev: Event) {
  ev.preventDefault();
  if (!email.trim()) return;
  sending = true;
  error = null;
  capture('login_attempted', { method: 'magic_link' });
  const { error: e } = await auth.signInWithEmail(email.trim());
  sending = false;
  if (e) {
    capture('login_failed', { method: 'magic_link', error: e });
    error = e;
  } else {
    sent = true;
    capture('magic_link_sent');
    showToast('Magic link sent — check your email', 'success');
  }
}
</script>

{#snippet signInBody()}
  <DialogHeader>
    <DialogTitle class="font-archivo font-bold text-lg">Sign in</DialogTitle>
  </DialogHeader>

  <div class="sub">
    {forced
      ? 'Sign in to access the site survey platform. PowerHive Kenya operators only.'
      : 'Only PowerHive Kenya operators can edit site data.'}
  </div>

  <Button variant="outline" class="w-full h-11 gap-2.5 justify-center" onclick={google}>
    <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true"
      ><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.4 5.4 2.6 13.2l7.8 6.1C12.2 13.3 17.6 9.5 24 9.5z" /><path
        fill="#4285F4"
        d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.5 3-2.2 5.5-4.7 7.2l7.3 5.7c4.3-4 6.8-9.9 6.8-17.4z"
      /><path fill="#FBBC05" d="M10.4 28.3c-.5-1.4-.7-2.9-.7-4.3s.3-2.9.7-4.3l-7.8-6.1C.9 16.7 0 20.2 0 24s.9 7.3 2.6 10.4l7.8-6.1z" /><path
        fill="#34A853"
        d="M24 48c6.2 0 11.4-2 15.2-5.5l-7.3-5.7c-2 1.4-4.6 2.2-7.9 2.2-6.4 0-11.8-3.8-13.6-9.3l-7.8 6.1C6.4 42.6 14.6 48 24 48z"
      /></svg
    >
    Sign in with Google
  </Button>
  <div class="caption">Restricted to @powerhive.com accounts.</div>

  <div class="or"><span>or magic link</span></div>

  {#if sent}
    <div class="sent">Check your inbox for a sign-in link.</div>
  {:else}
    <div class="helper">We'll email a one-time sign-in link — no password needed.</div>
    <form onsubmit={magicLink}>
      <input type="email" aria-label="Email address" placeholder="you@powerhive.com" bind:value={email} required />
      <Button type="submit" class="h-[42px]" disabled={sending}>{sending ? 'Please wait...' : 'Send magic link'}</Button>
    </form>
  {/if}

  {#if error}<div class="err">{error}</div>{/if}
{/snippet}

<DialogRoot open={forced || ui.authOpen} onOpenChange={(o) => { if (!o) close(); }}>
  {#if displacementEnabled}
    <DialogContent
      class="block top-0 left-0 h-dvh w-screen max-w-none translate-x-0 translate-y-0 gap-0 overflow-hidden rounded-none bg-background p-0 ring-0 sm:max-w-none"
      showCloseButton={!forced}
      escapeKeydownBehavior={forced ? 'ignore' : 'close'}
      interactOutsideBehavior={forced ? 'ignore' : 'close'}
    >
      <Displacement class="h-full w-full">
        <div class="stage">
          <div class="telemetry-bar top" aria-hidden="true">
            <span>LOC: 0.0236° S, 37.9062° E</span>
            <span>ELEV: 1,795M · GRID PH-KE</span>
            <span>SYS: SECURE</span>
          </div>

          <div class="card">{@render signInBody()}</div>

          <div class="telemetry-bar bottom" aria-hidden="true">
            <span>POWERHIVE KENYA · GEOLOC SITING PLATFORM</span>
            <span>EPSG:4326 · WGS84</span>
          </div>
        </div>
      </Displacement>
    </DialogContent>
  {:else}
    <DialogContent
      class="p-6 max-w-[380px] sm:max-w-[380px]"
      showCloseButton={!forced}
      escapeKeydownBehavior={forced ? 'ignore' : 'close'}
      interactOutsideBehavior={forced ? 'ignore' : 'close'}
    >
      {@render signInBody()}
    </DialogContent>
  {/if}
</DialogRoot>

<style>
  .stage {
    min-height: 100%;
    display: grid;
    place-items: center;
    padding: 32px 16px;
    position: relative;
    background-color: var(--bg);
    background-image:
      radial-gradient(ellipse 55% 45% at 50% 50%, color-mix(in srgb, var(--amber) 15%, transparent) 0%, transparent 70%),
      radial-gradient(circle 380px at 85% 15%, color-mix(in srgb, var(--cyan) 18%, transparent) 0%, transparent 70%),
      radial-gradient(circle 420px at 12% 88%, color-mix(in srgb, var(--amber) 12%, transparent) 0%, transparent 70%),
      radial-gradient(circle at center, transparent 40%, var(--bg) 95%);
    background-size: 100% 100%;
  }
  .telemetry-bar {
    position: absolute;
    left: 24px;
    right: 24px;
    display: flex;
    justify-content: space-between;
    font-family: 'IBM Plex Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.8px;
    color: var(--faint);
    text-transform: uppercase;
    pointer-events: none;
    user-select: none;
    opacity: 0.75;
  }
  .telemetry-bar.top {
    top: 20px;
  }
  .telemetry-bar.bottom {
    bottom: 20px;
  }
  .card {
    width: 380px;
    max-width: 100%;
    display: grid;
    gap: 16px;
    padding: 24px;
    border-radius: 14px;
    background: var(--surface);
    border: 1px solid var(--border);
    position: relative;
    z-index: 1;
  }
  .sub {
    font-size: 12.5px;
    color: var(--muted);
    margin-bottom: 20px;
  }
  .caption {
    font-size: 11.5px;
    color: var(--muted);
    text-align: center;
    margin-top: 10px;
  }
  .helper {
    font-size: 11.5px;
    color: var(--muted);
    margin-bottom: 10px;
  }
  .or {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 16px 0;
    color: var(--faint);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
  }
  .or::before,
  .or::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
  }
  form {
    display: flex;
    gap: 8px;
  }
  input {
    flex: 1;
    min-width: 0;
    height: 42px;
    padding: 0 13px;
    border-radius: 10px;
    border: 1px solid var(--border-soft);
    background: var(--surface-2);
    color: var(--fg);
    font: inherit;
    font-size: 13px;
  }
  .sent {
    font-size: 13px;
    color: #1f8f5f;
    background: rgba(63, 207, 142, 0.12);
    border-radius: 9px;
    padding: 12px;
    text-align: center;
  }
  .err {
    margin-top: 12px;
    font-size: 12.5px;
    color: #e0564a;
  }
</style>
